import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AemService } from 'abs-ngx-library/src/lib/core';
import { AuthService } from './auth.service';

@Injectable()
export class HtoAllocationService {
  private _window: any = window;

  constructor(
    private _http: HttpClient,
    private authService: AuthService,
    private aemService: AemService
  ) {}

  /**
   * POST to HTOAllocation API.
   * Only called when the page URL contains the query param `mf_hto=true`.
   *
   * @param offerId  The offer ID, sent as the POST request body: { "offerId": offerId }
   * @returns Observable with the API response
   */
  public allocate(offerId: string): Observable<any> {
    const storeId: string = this._window['AB']['userInfo']['j4u']['storeId'];
    const token: string = this._window['AB']['userInfo']['SWY_SHOP_TOKEN'];

    const envKey = this.getEnvironment();
    const config = (environment as any)[envKey].htoAllocationConfig;
    const url = this.authService.getApplicationDomainURL() + config.path + '?storeId=' + storeId;
    // const url = 'https://www-qa1.safeway.com' + config.path + '?storeId=' + storeId;

    const httpHeaders = new HttpHeaders({
      ...config.headers,
      'Authorization': `Bearer ${token}`
    });

    const body = { offerId };

    return this._http
      .post(url, body, { headers: httpHeaders })
      .pipe(
        catchError((error) => {
          console.error('HtoAllocationService: allocate API error', error);
          // Return a structured error object so the component can map the HTTP
          // status code AND optional response body error codes to a user-facing message.
          return of({
            error: true,
            status: error?.status ?? 0,
            // HttpErrorResponse exposes the parsed body in .error; fall back to
            // an empty object so callers can safely read nested properties.
            body: error?.error ?? {},
          });
        })
      );
  }

  /**
   * Returns true when BOTH conditions are met:
   *  1. The AEM feature flag `disableHtoAllocationFlag` is NOT set to true in
   *     window['SWY'].CONFIGSERVICE.featureToggleConfig (feature is enabled).
   *  2. The current page URL contains the query param `mf_hto=true` (case-insensitive).
   */
  public isMfHtoEnabled(): boolean {
    const isFeatureEnabled = !this.aemService.getFeatureFlagByNameDisable('disableHtoAllocationFlag');
    //const isFeatureEnabled = true; // --- IGNORE --- just for testing in QA until we have the feature flag set up in the environment
    const params = new URLSearchParams(this._window.location.search);
    const isMfHtoParam = params.get('mf_hto')?.toLowerCase() === 'true';
    return isFeatureEnabled && isMfHtoParam;
  }

  /**
   * Resolves the active environment key from the current hostname,
   * mirroring the same logic used in DiscoveryService.
   * Returns one of: 'local' | 'qa1' | 'qa2' | 'dev' | 'perf' | 'stage' | 'prod'
   */
  private getEnvironment(): string {
    const origin = window.location.origin;
    if (origin.indexOf('local') > 0) {
      return 'local';
    }
    if (origin.indexOf('qa1') > 0 || origin.indexOf('qa2') > 0 ||
        origin.indexOf('qa3') > 0 || origin.indexOf('stage') > 0) {
      return window.location.host.split('-')[1].split('.')[0];
    }
    if (origin.indexOf('dev') > 0) {
      return 'dev';
    }
    if (origin.indexOf('perf') > 0) {
      return 'perf';
    }
    return 'prod';
  }
}
